import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Divider, Typography } from "@mui/material";
import { ArrowLeft, ArrowRight, List } from "lucide-react";
import { Task } from "protogen/tasks_pb";
import { useListTasksForFamily } from "services/tasks";
import { notDoneStages } from "components/tasks/utils";
import { defaultTaskSorter } from "components/tasks/TasksTable";
import {
  TASK_PAGER_STATE_KEY,
  TaskPagerContext,
  TaskPagerEntry,
} from "components/tasks/taskPagerContext";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";

const PagerLink = ({
  entry,
  label,
  isNext,
  showTitle,
  context,
}: {
  entry: TaskPagerEntry;
  label: string;
  isNext: boolean;
  showTitle: boolean;
  context: TaskPagerContext;
}) => {
  const Arrow = isNext ? ArrowRight : ArrowLeft;
  return (
    <LinkRouter
      to={`/tasks/${encodeURIComponent(entry.ref)}`}
      navigationState={{ [TASK_PAGER_STATE_KEY]: context }}
      sx={{
        display: "flex",
        flexDirection: isNext ? "row-reverse" : "row",
        alignItems: "center",
        gap: "12px",
        minWidth: 0,
        padding: 0,
      }}
    >
      <Arrow size={20} style={{ flexShrink: 0, color: "#198282" }} />
      <Box sx={{ minWidth: 0, textAlign: isNext ? "right" : "left" }}>
        <Typography
          variant="body"
          sx={{ display: "block", color: "primary.main", fontWeight: 600 }}
        >
          {label}
        </Typography>
        {showTitle && (
          <Typography
            variant="bodySmall"
            sx={{
              display: "block",
              color: "text.tertiary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {entry.title}
          </Typography>
        )}
      </Box>
    </LinkRouter>
  );
};

export default ({ task }: { task: Task }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [familyTasks, setFamilyTasks] = useState<Task[] | null>(null);
  const familyRef = task.family?.ref || task.familyRef;
  const familyName = task.family?.name || "Family";
  const { request } = useListTasksForFamily((r) => setFamilyTasks(r.tasks));

  const carriedContext: TaskPagerContext | undefined =
    location.state?.[TASK_PAGER_STATE_KEY];
  const isCarriedContextUsable = !!carriedContext?.entries.some(
    (e) => e.ref === task.ref,
  );

  useEffect(() => {
    if (isCarriedContextUsable || !familyRef) return;
    request({ familyRef, taskStates: [], taskStages: notDoneStages });
  }, [isCarriedContextUsable, familyRef]);

  const fallbackContext: TaskPagerContext | undefined = useMemo(() => {
    if (!familyTasks || !familyRef) return undefined;
    return {
      listName: `${familyName} open tasks`,
      listPath: `/families/${encodeURIComponent(familyRef)}#tab-tasks`,
      entries: [...familyTasks]
        .sort(defaultTaskSorter)
        .map((t) => ({ ref: t.ref, title: t.title })),
    };
  }, [familyTasks, familyRef, familyName]);

  const context = isCarriedContextUsable ? carriedContext : fallbackContext;
  const currentIndex =
    context?.entries.findIndex((e) => e.ref === task.ref) ?? -1;
  if (!context || currentIndex < 0) return null;

  const previousEntry =
    currentIndex > 0 ? context.entries[currentIndex - 1] : null;
  const nextEntry =
    currentIndex < context.entries.length - 1
      ? context.entries[currentIndex + 1]
      : null;
  if (!previousEntry && !nextEntry) return null;

  return (
    <Box sx={{ marginTop: "32px" }}>
      <Divider sx={{ marginBottom: "20px" }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: isMobile ? "8px" : "16px",
        }}
      >
        <Box sx={{ display: "flex", minWidth: 0 }}>
          {previousEntry && (
            <PagerLink
              entry={previousEntry}
              label="Previous task"
              isNext={false}
              showTitle={!isMobile}
              context={context}
            />
          )}
        </Box>
        <LinkRouter
          to={context.listPath}
          sx={{ alignItems: "center", gap: "8px", padding: 0, minWidth: 0 }}
        >
          <List size={20} style={{ flexShrink: 0, color: "#3D3D3D" }} />
          <Box sx={{ minWidth: 0, textAlign: "center" }}>
            <Typography
              variant="body"
              sx={{
                display: "block",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {context.listName}
            </Typography>
            <Typography
              variant="bodySmall"
              sx={{ display: "block", color: "text.tertiary" }}
            >
              {currentIndex + 1} of {context.entries.length}
            </Typography>
          </Box>
        </LinkRouter>
        <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
          {nextEntry && (
            <PagerLink
              entry={nextEntry}
              label="Next task"
              isNext={true}
              showTitle={!isMobile}
              context={context}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
